import { useCallback, useState } from "react";
import { useToast } from "@/shared/ui";
import { uploadModel, type VaultModel } from "./vaultApi";

export type UploadStage = "uploading" | "converting";

export interface UploadEntry {
  id: string;
  name: string;
  stage: UploadStage;
  /** Coarse progress (the upload pipeline reports discrete stages, not bytes). */
  progress: number;
}

const STAGE_PROGRESS: Record<UploadStage, number> = { uploading: 45, converting: 85 };

let _uid = 0;
const nextId = () => `up_${++_uid}_${Date.now()}`;

/**
 * Shared upload flow for the Home drop-zone and the Drive. Tracks in-flight
 * uploads (for progress rows), surfaces failures as toasts, and calls `onDone`
 * after each batch so the caller can refresh its listing.
 */
export function useUploads(onDone?: () => void) {
  const { toast } = useToast();
  const [uploads, setUploads] = useState<UploadEntry[]>([]);

  const uploadFiles = useCallback(
    async (files: FileList | File[] | null, folderId: string | null) => {
      const list = files ? Array.from(files) : [];
      if (!list.length) return;
      let anyOk = false;

      for (const file of list) {
        const id = nextId();
        setUploads((u) => [...u, { id, name: file.name, stage: "uploading", progress: STAGE_PROGRESS.uploading }]);
        try {
          const model: VaultModel = await uploadModel(file, folderId, (stage) =>
            setUploads((u) => u.map((e) => (e.id === id ? { ...e, stage, progress: STAGE_PROGRESS[stage] } : e))),
          );
          anyOk = true;
          if (model.status === "error") {
            toast({ tone: "error", title: file.name, description: model.statusMessage || "Processing failed" });
          }
        } catch (err) {
          toast({
            tone: "error",
            title: `Couldn't upload ${file.name}`,
            description: err instanceof Error ? err.message : "Upload failed",
          });
        } finally {
          setUploads((u) => u.filter((e) => e.id !== id));
        }
      }

      if (anyOk) {
        toast({ tone: "success", title: list.length > 1 ? `${list.length} files uploaded` : "Upload complete" });
        onDone?.();
      }
    },
    [toast, onDone],
  );

  return { uploads, uploadFiles };
}
